import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    whatsapp: '',
    location: '',
    bank_name: '',
    bank_account_number: '',
    bank_account_name: '',
    referral_code: '',
  });

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData((prev) => ({ ...prev, referral_code: refCode }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.register(formData);
      const { token, user, role } = response.data;
      
      login(token, user, role);
      toast.success('Registration successful! Welcome to Medichi Tribe.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-background px-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary-900 flex items-center justify-center">
            <span className="text-white font-serif text-2xl font-semibold">M</span>
          </div>
          <span className="font-serif text-3xl font-medium text-primary-900">Medichi Tribe</span>
        </Link>

        <Card className="border-neutral-border shadow-card animate-fade-in">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-2xl text-primary-900 flex items-center justify-center gap-2">
              <UserPlus className="h-6 w-6" />
              Join the Tribe
            </CardTitle>
            <CardDescription className="text-neutral-text-muted">
              Create your account and start building your network
            </CardDescription>
            {formData.referral_code && (
              <div className="mt-3 inline-block px-4 py-2 bg-primary-50 rounded-full">
                <span className="text-sm text-primary-700">
                  Referred by code: <strong>{formData.referral_code}</strong>
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-neutral-text-muted">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-xl"
                      data-testid="register-fullname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-xl"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp Number *</Label>
                    <Input
                      id="whatsapp"
                      name="whatsapp"
                      placeholder="+234 800 000 0000"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-xl"
                      data-testid="register-whatsapp-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      name="location"
                      placeholder="City, State/Country"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-xl"
                      data-testid="register-location-input"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-neutral-text-muted">
                  Bank Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank Name *</Label>
                    <Input
                      id="bank_name"
                      name="bank_name"
                      placeholder="GTBank"
                      value={formData.bank_name}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-xl"
                      data-testid="register-bankname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_number">Account Number *</Label>
                    <Input
                      id="bank_account_number"
                      name="bank_account_number"
                      placeholder="1234567890"
                      value={formData.bank_account_number}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-xl"
                      data-testid="register-accountnumber-input"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bank_account_name">Account Holder Name *</Label>
                    <Input
                      id="bank_account_name"
                      name="bank_account_name"
                      placeholder="John Doe"
                      value={formData.bank_account_name}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-xl"
                      data-testid="register-accountname-input"
                    />
                  </div>
                </div>
              </div>

              {/* Referral Code (optional, pre-filled if from link) */}
              <div className="space-y-2">
                <Label htmlFor="referral_code">
                  Referral Code {searchParams.get('ref') ? '(From your referrer)' : '(Optional)'}
                </Label>
                {searchParams.get('ref') ? (
                  <div className="flex items-center gap-2">
                    <Input
                      id="referral_code"
                      name="referral_code"
                      value={formData.referral_code}
                      readOnly
                      className="h-11 rounded-xl bg-green-50 border-green-300 font-mono font-bold text-green-700"
                      data-testid="register-referral-input"
                    />
                    <span className="text-green-600 text-sm whitespace-nowrap">✓ Applied</span>
                  </div>
                ) : (
                  <Input
                    id="referral_code"
                    name="referral_code"
                    placeholder="Enter referral code if you have one"
                    value={formData.referral_code}
                    onChange={handleChange}
                    className="h-11 rounded-xl"
                    data-testid="register-referral-input"
                  />
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary-900 hover:bg-primary-800 text-white rounded-xl font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-neutral-text-muted text-sm">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-primary-700 hover:text-primary-900 font-medium"
                  data-testid="login-link"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
